<template>
  <div class="lp-checkbox-group-wrapper xpx-4 xpt-4">
    <slot name="parentLabel" />
    <lp-checkbox
      v-if="showParent"
      style="font-weight: bold"
      :model-value="allChecked"
      :type="labelType"
      :label="label"
      :after-icon="parentAfterIcon"
      :sub-label="parentSubLabel"
      :disabled="isDisabled"
      data-ua-multiple-id="ua-checkboxgroup-parent"
      :label-class="parentLabelClass"
      :without-checkbox="withoutParentCheckbox"
      :data-ua-value="label"
      @change="onGroupCheck"
    />
    <div
      v-if="showChildren"
      class="lp-checkbox-group"
      :class="showParent && 'ml-1'"
    >
      <lp-checkbox
        v-for="(option, index) in options"
        :key="index"
        :model-value="option.disabled ? false : isSelected(option)"
        :label="getLabel(option)"
        :disabled="option.disabled"
        data-ua-multiple-id="ua-checkboxgroup-child"
        :data-ua-value="getLabel(option)"
        :type="getOptionType(option)"
        :search-includes-children="searchIncludesChildren"
        :parent="label"
        @update:model-value="onToggle(option)"
      >
        <template #pre>
          <slot name="decorator" :option="option" />
        </template>
        <template #label>
          <span v-html="option.labelHtml"></span>
        </template>
      </lp-checkbox>
    </div>
  </div>
</template>

<script setup lang="ts">
import { watch, toRef, inject, computed, ref } from 'vue'
import { Colors } from '@/models/enums'
import { ParentBehaviour } from '@/components/checkbox-group/types'
import { t } from '@/plugins/i18n'

defineOptions({
  name: 'lp-checkbox-group',
})

interface Props {
  label?: string
  labelType?: Colors
  id?: number | null
  groupValue?: object
  options: object[]
  showParent?: boolean
  parentBehaviour?: ParentBehaviour
  searchIncludesChildren?: boolean
  parentSubLabel?: string | null
  parentAfterIcon?: string | null
  parentLabelClass?: string | null
  withoutParentCheckbox?: boolean
  uaLocatorOption?: string
}

const props = withDefaults(defineProps<Props>(), {
  label: t('Shared.All'),
  labelType: Colors.PRIMARY,
  id: null,
  groupValue: undefined,
  options: undefined,
  showParent: true,
  parentBehaviour: ParentBehaviour.SelectAll,
  searchIncludesChildren: false,
  parentSubLabel: null,
  parentAfterIcon: null,
  parentLabelClass: null,
  withoutParentCheckbox: false,
  uaLocatorOption: 'ua-checkboxgroup-option',
})

const emit = defineEmits(['update:model-value', 'onParentToggle'])

const options = toRef(props, 'options')
const groupValue = toRef(props, 'groupValue')
const addOptions = inject('addOptions', () => {}) as any
const onToggle = inject('onToggle', () => {}) as any
const onGroupToggle = inject('onGroupToggle', () => {}) as any
const isSelected = inject('isSelected', () => {}) as any
const isGroupSelected = inject('isGroupSelected', () => false) as any
const getLabel = inject('getLabel', () => {}) as any
const getOptionType = inject('getOptionType', () => {}) as any
const isParentChecked = ref(false)
const allChecked = computed(
  () =>
    isGroupSelected(
      options.value?.filter((option) => !option.disabled),
      props.groupValue,
    ) || isParentChecked.value,
)

const showChildren = ref(
  props.parentBehaviour !== ParentBehaviour.ShowChildrenOnSelect,
)

// Let the parent know which options belong to this group
watch(
  [options, groupValue],
  () => {
    // TODO: Should we check for a race condition or multiple calls of this callback?
    if (groupValue.value) {
      addOptions([...options.value, groupValue.value])
    } else {
      addOptions(options.value)
    }
  },
  { immediate: true },
)

function onGroupCheck() {
  let selectedOptions = []

  if (props.parentBehaviour === ParentBehaviour.SelectAll) {
    // Empty parent scenario
    if (!options.value.length && groupValue.value) {
      selectedOptions = [groupValue.value]
    } else {
      selectedOptions = options.value
    }
    onGroupToggle(allChecked.value, selectedOptions, props.id)
  }

  if (props.parentBehaviour === ParentBehaviour.ShowChildrenOnSelect) {
    showChildren.value = !showChildren.value
    isParentChecked.value = !isParentChecked.value
  }

  if (props.parentBehaviour === ParentBehaviour.AllowParentSelect) {
    isParentChecked.value = !isParentChecked.value
    onToggle({ id: props.id })
  }

  emit('onParentToggle')
}

const isDisabled = computed(() => {
  return options.value?.length
    ? Boolean(options.value?.every((option) => option.disabled))
    : false
})
</script>
